// Pop's line bank, ported from the Godot game (scripts/narrative/pop_lines.gd). STORY_BIBLE §9
// governs every line: deadpan, Pop never lies (he stops short), every joke survives the re-read.
// The three hard bans from the Godot header still hold: no real platform/network words in his
// mouth, he never uses an account name (only "partner", sparingly), no dropped-g dialect.
// Lines about Sparks, prices and the vault are left out: there's no spend currency in this build.

export const GREETINGS = [
  "What'll it be? Boards at dawn. Dawn's whenever you stand up.",
  "Evening. Poster came in on the wire. Ugly one. They're all ugly ones.",
  'You look rested. We can fix that.',
  "Ration's on the bar. Don't spend it all in one glass.",
  'Company says morale is up four points. You\'d know better than me.',
  'Sit anywhere. Everybody does, eventually.',
  "You're early. Or the train's late. Only one of those is allowed.",
  "Glass is clean. That's the only promise I make before noon.",
  "There's coffee. There's water too, but that's above your grade and mine.",
  "You came back. I'll stop being surprised by that eventually.",
  "Take the stool with the dent in it. That one's honest.",
  "Quiet car tonight. Quiet isn't the same as empty.",
  'Whatever they told you up front, I heard it first and forgot it faster.',
  "I kept your seat. Nobody was fighting me for it.",
  'Good glass or fast glass?',
  'Long ride out. Longer one back, if you count it honest.',
];

/** The very first thing Pop says to someone new. */
export const GREETING_FIRST = "New face. I'm the bartender. Folks call me Pop. The Company calls me a retention asset. What'll it be?";
/** Over the room on a first boarding, before anyone has walked up to the bar. */
export const ARRIVAL_FIRST = "First ride's whenever you're ready. Come see me at the bar.";

/** "What's the job?": the run explained in the fiction, one beat per ask, in order. */
export const JOB = [
  'Company sets you down on a colony. Machines there are what they call defective. You clear them.',
  'When an island\'s done, the swinging doors at the far end light up. Walk through. They take you to the next one.',
  "Last island's got the keeper. Big unit, sitting on the town's water. There's a poster.",
  'Keeper goes down, somebody rings the bell. Then you hold the ground till the train comes for the water. Then you come for a drink.',
  "If it goes wrong, you'll end up back here. Everybody does. Don't ask me how. Eat something.",
];

export const POUR = [
  "Glass, cloth, glass. That's the trade.",
  'Somebody\'s got to keep count.',
  "Third time I've cleaned this same ring.",
  'Enough ice for tonight. Barely.',
  'Nobody drinks the water. Nobody asks why.',
  'Good stock tonight. Somebody upstairs made an error.',
  "One more shift, then one more shift.",
  "Wind out there's got nothing on the paperwork.",
  "Six-Shooter Franks. 'Now With More Loyalty.' The Company writes its own slogans. Nobody edits them.",
  "I don't drink the water I serve. Doctor's orders. The doctor was also me.",
];

export const POSSE = [
  "Riders, huh. Four guns come home more often than one. That's just arithmetic.",
  'Bring company. The ones who ride alone stop coming in.',
  "Four stools, four riders. That's not superstition, that's the table.",
  "I'll hold four glasses back. If I'm wrong I'll drink the difference.",
];

export const LOADOUT_DONE = [
  "That iron'll do. It's not the iron that comes back, anyhow.",
  'Fine kit. Try to bring most of it home attached.',
  "Good pick. The gun matters less than knowing when to feed it. Reload's the expensive part out there.",
  "That'll do. It always does, right up until it doesn't.",
  'Good weight on you. Carry what you can still run with.',
];

/** "Show me the rack.": Pop points over your shoulder at the gun rack by the door. */
export const RACK_POINT = [
  "Rack's by the door. Company issue. Take what you can carry home.",
  "Over there. They count them when you're back. Mostly.",
  "Behind you, by the door. Pick something you'll still like on the ride home.",
  "Rack. Every one of them works. That's in writing.",
];

/** You've finished talking; you're still on the stool. */
export const FAREWELL = [
  "I'll be here. That's most of the job.",
  "Holler. Or don't. I'll be here either way.",
  "Stool's yours till the roof opens.",
  "Take your time. The glass isn't going anywhere.",
];

/** "Let's see my paperwork." */
export const PAPERWORK = [
  "Your file. Thicker than it was. They always are.",
  "Paperwork. Signed, stamped, and nobody read it. Same as mine.",
];

/** what your cowboy says back (naive-proud, STORY_BIBLE §9.5): the reply choices at the bar */
export const YOU = {
  saddle: 'Saddle me up.',
  unready: 'Hold up a minute.',
  rack: 'Show me the rack.',
  job: "What's the job?",
  pour: 'Pour me one.',
  paper: "Let's see my paperwork.",
  bye: "That's all for now.",
};

/** One line, then the roof opens. */
export const SADDLE = [
  "Poster's yours. First one's water when you're back.",
  "Ride safe. That's not policy. Policy doesn't care. That's just me.",
  "Go on, then. I'll keep the stool warm and the warmer warm.",
  "Clean ticket, clean ride. Bring me a story I can't repeat.",
  "The train doesn't wait. Per policy. Neither should you.",
  "I'll leave the light on over your stool. Costs the Company nothing.",
  "Out you go. Don't read the fine print out there. Read the ground.",
  "Whatever's on that island was somebody's before it was a job.",
  "Come back thirsty. That's the only part of this I control.",
  'Go get the Company its water. Then come drink mine.',
];

/** Somebody said they're ready but the posse isn't. */
export const WAITING = [
  "You're cleared. The rest of them aren't. The roof only opens once.",
  "Sit tight. Company doesn't launch half a posse. It's not kindness, it's fuel.",
  "Waiting on your people. They'll come or they won't. Mostly they come.",
];
export const UNREADY = [
  "Suit yourself. The island isn't going anywhere. That's sort of the point of it.",
  'Take your time. The Company charges for it, but take it.',
];

/** Wipe return: you're already at the bar, mid-bite. No explanation offered. Ever. */
export const WIPE_RETURN = 'Welcome back.';

/** After an extraction. */
export const VISIT_FLAVOR = [
  'Clean ticket. Company says thanks. Company says that to everybody.',
  "Heard the extraction went loud. It always goes loud. Quiet costs extra.",
  "Warmer's full tonight. Eat something.",
  'Boots by the door. The rug\'s seen worse.',
  "They filed your ticket in eleven minutes. Fastest thing the Company does.",
  "You've got dust on you from a town that had a name. It's on the poster, if you want it.",
  "Rail's smooth. Enjoy that. Smooth means we're not stopping.",
  "There's a stool free at the end. There's always a stool free at the end.",
  'Glass, or the good glass? You came back, so. The good one.',
  "You didn't ask what happens to the towns after. Most don't.",
];

/** Pop to the room while nobody's talking to him. */
export const MUTTER = [
  'Rail\'s running quiet.',
  "Warmer needs topping up. It always does.",
  "Company clock says one thing. Bones say another.",
  "Poster's crooked. Leave it.",
  "That stool's been empty a while now.",
  "Boiler's up. Means we're moving.",
];

export function pick<T>(list: readonly T[], salt = 0): T {
  return list[Math.floor((Math.random() * 997 + salt) % list.length)];
}

/** Deterministic rotation per key, so Pop doesn't repeat himself back to back. */
const rot = new Map<readonly string[], number>();
export function next(list: readonly string[]): string {
  const i = rot.get(list) ?? Math.floor(Math.random() * list.length);
  rot.set(list, (i + 1) % list.length);
  return list[i % list.length];
}
